/*
 * The reading surface for one inbound email: a document for a sandboxed `srcdoc` frame. Pure, so
 * each guard is testable without a browser. Three layers, any one of which blocks script:
 *
 * 1. `sanitizeInboundEmailHtml` has already removed script, handlers, forms, frames and unsafe URLs.
 * 2. The sandbox has no `allow-scripts`, so nothing in the frame runs, inline handlers included.
 * 3. The document's Content-Security-Policy allows no script, network, form or frame source, and
 *    images only as inline data until the rep loads them.
 *
 * `allow-same-origin` is there so the application can read the rendered height and size the frame to
 * the email; without it every message would scroll inside a fixed box, because a sandboxed frame
 * that runs no script cannot report its own size. It is safe only without `allow-scripts`: the pair
 * together would let the email remove its own sandbox. The test for this constant fails if
 * `allow-scripts` is ever added. `allow-popups-to-escape-sandbox` makes a clicked link open as an
 * ordinary page in a new tab rather than a crippled sandboxed one.
 */
export const EMAIL_FRAME_SANDBOX = "allow-same-origin allow-popups allow-popups-to-escape-sandbox";

export function emailFramePolicy(options: { images: boolean }): string {
  return [
    "default-src 'none'",
    "script-src 'none'",
    "object-src 'none'",
    "frame-src 'none'",
    "child-src 'none'",
    "connect-src 'none'",
    "font-src 'none'",
    "media-src 'none'",
    "manifest-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
    "style-src 'unsafe-inline'",
    options.images ? "img-src data: https:" : "img-src data:",
  ].join("; ");
}

/*
 * Hidden until asked for: the quoted tail `foldQuotedHistory` marked (`quoted-history.ts`). Only
 * those marks hide anything, so a message detection is unsure about shows in full.
 */
const QUOTED_HISTORY_HIDDEN = "[data-lcr-quote]{display:none!important}";

/*
 * Three behaviours (`email-styling.ts` decides plain or styled). Plain mail, whose only colours were
 * neutral and have been removed, reads in the application's theme on the message card behind it.
 * Mail its author styled keeps its own colours on a light page in the light theme; in the dark theme
 * its colours are rewritten for the dark page (`email-dark.ts`) and it frames like plain mail, until
 * the rep asks for the original light page. No look puts a filter over the email or its pictures.
 * Mail wider than the frame is scaled to fit by the page (`fitEmail` in the client); the sideways
 * scroll is the fallback if that has not run.
 */
/*
 * An image with no `src` is either held back until the rep loads remote images or had a source the
 * sanitizer could not keep (`cid:` attachments, plain `http:`). Left alone, the browser draws a
 * broken-image icon and the alt text inside whatever box the email's own styles give it, which in a
 * signature reads as letters stacked one per line in a coloured pill. `withImagePlaceholders` gives
 * each one an empty picture with the image's declared proportion, and this style makes it a quiet
 * box no taller than a line of text: a stand-in says an image is there, it does not reserve the
 * image's space. Classic Outlook declares signature icons as 336 by 478 with a height of 4.98
 * inches, which at the earlier 120 px cap left a grey pillar per icon. Width and height follow the
 * proportion under the caps. The email's colours are overridden only here.
 */
const HELD_IMAGE_STYLE =
  "img[data-placeholder]{width:auto!important;height:auto!important;" +
  "max-width:min(100%,160px);max-height:40px;" +
  "background:#ebe8e1!important;border:0!important;border-radius:4px!important;vertical-align:middle}";

/**
 * Transparent, in the image's declared proportion, and sized as the stand-in should appear: the
 * declared size scaled down to fit 160 by 40 px, or 24 px square when the email declares none. The
 * size must be the picture's own: inside a shrink-to-fit table cell, as signatures are, a sizeless
 * picture resolves to nothing. Single quotes only, so it sits safely inside a double-quoted `src`.
 */
function placeholderSrc(attributes: string): string {
  const width = Number(/\swidth="(\d{1,4})"/.exec(attributes)?.[1] ?? 0);
  const height = Number(/\sheight="(\d{1,4})"/.exec(attributes)?.[1] ?? 0);
  const declared = width > 0 && height > 0;
  const scale = declared ? Math.min(1, 160 / width, 40 / height) : 1;
  const [w, h] = declared
    ? [Math.max(8, Math.round(width * scale)), Math.max(8, Math.round(height * scale))]
    : [24, 24];
  return `data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27${w}%27 height=%27${h}%27 viewBox=%270 0 ${w} ${h}%27/%3E`;
}

/*
 * Same guarantee as `withRemoteImages`: the sanitizer quotes every attribute, so an `<img` tag ends
 * at its first `>` and a tag without ` src="` has no source. Only such tags change.
 */
export function withImagePlaceholders(html: string): string {
  return html.replaceAll(/<img\b([^>]*?)(\s*\/?)>/g, (tag, attributes: string, close: string) =>
    /\ssrc="/.test(attributes)
      ? tag
      : `<img${attributes} src="${placeholderSrc(attributes)}" data-placeholder=""${close}>`,
  );
}

/**
 * `email`: the author's own light page. `app-light` and `app-dark`: plain mail in the theme.
 * `email-dark`: styled mail with its colours made for the dark page, framed as `app-dark`.
 */
export type EmailFrameLook = "email" | "email-dark" | "app-light" | "app-dark";

/*
 * The `--lcr-*` pairs from styles.css a plain email reads in, as `[light, dark]`, and `sheet`, where a
 * styled email's white ground lands in the dark: a frame's document cannot inherit the page's custom
 * properties. `email-styling.test.ts` fails if these drift.
 */
export const FRAME_TOKENS = {
  sheet: ["#ffffff", "#302d28"],
  ink: ["#212121", "#f6f1e8"],
  muted: ["#6e6860", "#c4bdb1"],
  line: ["#e5dcc7", "#4d463b"],
  "line-strong": ["#d3c7ab", "#84765e"],
  warn: ["#7d5a12", "#f8da91"],
} as const;

const FRAME_LAYOUT =
  "html{margin:0;overflow:hidden}" +
  "body{box-sizing:border-box;margin:0;padding:12px 14px;overflow-x:auto;overflow-y:hidden;" +
  "font:14px/1.55 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;" +
  "overflow-wrap:anywhere}" +
  "img{max-width:100%;height:auto}pre{white-space:pre-wrap}" +
  HELD_IMAGE_STYLE +
  "blockquote{margin:0 0 0 .8ex;padding-left:1ex;border-left:2px solid}" +
  // Shown quoted history starts under a rule; mail spaced by blank paragraphs keeps its own rhythm.
  '[data-lcr-quote="start"]{margin-top:12px!important;padding-top:10px!important;border-top:1px solid!important}' +
  "p[data-lcr-tight]{margin-top:0;margin-bottom:0}";

type FrameColours = {
  scheme: "light" | "dark";
  page: string;
  text: string;
  link: string;
  rule: string;
  divider: string;
  quoted: string;
};

const frameColours = (colours: FrameColours): string =>
  `:root{color-scheme:${colours.scheme}}html,body{background:${colours.page}}` +
  `body{color:${colours.text}}a[href]{color:${colours.link}}` +
  `blockquote{border-left-color:${colours.rule};color:${colours.quoted}}` +
  `[data-lcr-quote="start"]{border-top-color:${colours.divider}!important}` +
  `[data-lcr-quote]{color:${colours.quoted}}`;

/** Plain mail is words on the card, so it starts on the card's own edge and adds no blank lines. */
export const PLAIN_ON_CARD =
  "body{padding:0}body>:first-child{margin-top:0}body>:last-child{margin-bottom:0}";

function lookStyle(look: EmailFrameLook): string {
  if (look === "email")
    return frameColours({
      scheme: "light",
      page: "#fff",
      text: "#1f2328",
      link: "#0b57d0",
      rule: "#d0d7de",
      divider: "#e3ded3",
      quoted: "#57606a",
    });
  const pick = look === "app-light" ? 0 : 1;
  const colours = frameColours({
    scheme: pick === 1 ? "dark" : "light",
    // The message card shows through: a received message on `sheet`, ours on `nav`.
    page: "transparent",
    text: FRAME_TOKENS.ink[pick],
    link: FRAME_TOKENS.warn[pick],
    rule: FRAME_TOKENS["line-strong"][pick],
    divider: FRAME_TOKENS.line[pick],
    quoted: FRAME_TOKENS.muted[pick],
  });
  const onCard = look === "email-dark" ? "" : PLAIN_ON_CARD;
  // A held-back picture's stand-in sits on the message card, so it takes the theme's hairline.
  return `${colours}${onCard}a[href]{text-underline-offset:3px}img[data-placeholder]{background:${FRAME_TOKENS.line[pick]}!important}`;
}

export function hasRemoteImages(html: string): boolean {
  return /\sdata-remote-src="/.test(html);
}

/*
 * The sanitizer's output quotes every attribute and escapes quotes inside values, and text cannot
 * contain `<`, so this rewrite can only turn a text run into another text run or a kept image
 * address into a `src`. Every address it exposes was checked as absolute https when it was stored.
 */
export function withRemoteImages(html: string): string {
  return html.replaceAll(/(<img\b[^>]*?\s)data-remote-src="/g, '$1src="');
}

export function emailFrameDocument(
  sanitizedHtml: string,
  options: { images: boolean; quoted: boolean; look?: EmailFrameLook },
): string {
  const body = withImagePlaceholders(
    options.images ? withRemoteImages(sanitizedHtml) : sanitizedHtml,
  );
  return [
    '<!doctype html><html><head><meta charset="utf-8">',
    `<meta http-equiv="Content-Security-Policy" content="${emailFramePolicy(options)}">`,
    '<meta name="referrer" content="no-referrer">',
    `<style>${FRAME_LAYOUT}${lookStyle(options.look ?? "email")}${options.quoted ? "" : QUOTED_HISTORY_HIDDEN}</style>`,
    `</head><body>${body}</body></html>`,
  ].join("");
}
